import { Link } from "react-router-dom";
import { ArrowRight, Clock, Users, Award, BookOpen, CheckCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const defaultCourses = [
  {
    title: "ISO 27001 Lead Auditor Training",
    duration: "5 Days",
    mode: "Online / On-site",
    certification: "Certified Lead Auditor",
    desc: "Comprehensive training program covering the principles and practices of auditing an Information Security Management System (ISMS) against ISO/IEC 27001:2022.",
    topics: ["ISO 27001:2022 requirements and Annex A controls", "Audit planning, preparation and execution", "Non-conformity reporting and corrective actions", "Certification audit methodology", "Risk-based auditing techniques"],
  },
  {
    title: "ISO 9001 Lead Auditor Training",
    duration: "5 Days",
    mode: "Online / On-site",
    certification: "Certified Lead Auditor",
    desc: "Train to audit Quality Management Systems against ISO 9001:2015. Learn process-based auditing techniques and develop the skills to lead audit teams.",
    topics: ["ISO 9001:2015 standard requirements", "Process approach to quality management", "Audit techniques and evidence gathering", "Audit reporting and follow-up", "Continual improvement methodologies"],
  },
  {
    title: "ISO 22301 Lead Implementer",
    duration: "5 Days",
    mode: "Online / On-site",
    certification: "Lead Implementer",
    desc: "Master the implementation of a Business Continuity Management System aligned with ISO 22301:2019. Build organizational resilience against disruptions.",
    topics: ["Business impact analysis (BIA)", "Risk assessment for business continuity", "BCMS planning and implementation", "Testing and exercising BC plans", "Measurement and continual improvement"],
  },
  {
    title: "Cybersecurity Fundamentals",
    duration: "3 Days",
    mode: "Online",
    certification: "Certificate of Completion",
    desc: "An introductory course for professionals who need to understand cybersecurity principles, threats, and defensive strategies within their organization.",
    topics: ["Cybersecurity landscape and threat intelligence", "Network security fundamentals", "Social engineering and phishing awareness", "Incident response basics", "Security governance frameworks"],
  },
  {
    title: "Risk Management Workshop",
    duration: "2 Days",
    mode: "Online / On-site",
    certification: "Certificate of Completion",
    desc: "Practical workshop on enterprise risk management aligned with ISO 31000. Learn to identify, assess, and treat organizational risks effectively.",
    topics: ["ISO 31000 risk management framework", "Risk identification and classification", "Qualitative and quantitative risk analysis", "Risk treatment and acceptance criteria", "Risk monitoring and reporting"],
  },
  {
    title: "Security Awareness for Employees",
    duration: "1 Day",
    mode: "Online / On-site",
    certification: "Certificate of Completion",
    desc: "Organization-wide security awareness training designed to educate all employees on cybersecurity best practices and their role in protecting company assets.",
    topics: ["Password security and authentication", "Recognizing phishing and social engineering", "Safe internet and email practices", "Data handling and classification", "Reporting security incidents"],
  },
];

export default function TrainingPage() {
  const { t } = useLang();
  const tp = t?.training_page || {};
  const tCourses = tp.courses_detail || [];

  const courses = defaultCourses.map((def, i) => {
    const tc = tCourses[i];
    return {
      title: tc?.title || def.title,
      duration: tc?.duration || def.duration,
      mode: tc?.mode || def.mode,
      certification: tc?.certification || def.certification,
      desc: tc?.desc || def.desc,
      topics: tc?.topics || def.topics,
    };
  });

  return (
    <div className="page-transition pt-20" data-testid="training-page">
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{tp.badge || "Training"}</p>
          <h1 data-testid="training-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tp.title || "Professional Training Programs"}
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            {tp.subtitle || "Build expertise in ISO management systems, cybersecurity, and risk management with our accredited training courses."}
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-0 border border-[#0B2545]/10">
            {courses.map((course, i) => (
              <div key={course.title} data-testid={`training-course-${i}`} className={`grid grid-cols-1 lg:grid-cols-2 gap-0 border-b border-[#0B2545]/10 last:border-b-0 ${i % 2 === 1 ? 'bg-[#F5F7FA]' : 'bg-white'}`}>
                <div className="p-8 lg:p-12">
                  <h2 className="text-xl font-bold text-[#0B2545] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>{course.title}</h2>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><Clock size={14} className="text-[#13A8BD]" /> {course.duration}</span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><Users size={14} className="text-[#13A8BD]" /> {course.mode}</span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><Award size={14} className="text-[#13A8BD]" /> {course.certification}</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{course.desc}</p>
                </div>
                <div className="p-8 lg:p-12 border-l border-[#0B2545]/10">
                  <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">
                    <BookOpen size={14} className="inline mr-2" />{tp.topics_label || "Course Topics"}
                  </h4>
                  <ul className="space-y-2.5">
                    {course.topics.map((topic) => (
                      <li key={topic} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <CheckCircle size={14} className="text-[#13A8BD] mt-0.5 flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F5F7FA] border-t border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tp.cta_title || "Interested in Our Training Programs?"}
          </h2>
          <p className="text-base text-slate-600 max-w-xl mx-auto mb-10">
            {tp.cta_desc || "Contact us for upcoming course schedules, group discounts, and customized corporate training programs."}
          </p>
          <Link to="/contact" data-testid="training-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            {tp.cta_button || "Enquire Now"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
